import { useState } from "react";
import {
  MapPinIcon,
  PhoneIcon,
  GlobeAltIcon,
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";
import FacilityAvatar from "../shared/FacilityAvatar";
import StatusBadge from "../shared/StatusBadge";
import { useFacilityAdmins } from "../../hooks/useFacilityAdmins";
import { formatPhoneDisplay } from "../../utils/phoneMask";
import { COUNTRIES } from "../../constants/facility.constants";

const initials = (name = "") => name.trim().slice(0, 2).toUpperCase();

const formatLocalTime = (timezone) => {
  try {
    return new Date().toLocaleTimeString("en-US", { timeZone: timezone, hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
  } catch (error) {
    return new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  }
};

const formatCreatedDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

const FacilityHeaderCard = ({ facility, onEdit, onToggleActive }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const admins = useFacilityAdmins(facility.facility_number);
  const logoUrl = facility.logo_urls?.desktop_app_logo || facility.logo_urls?.light_theme_logo;
  const createdDate = formatCreatedDate(facility.created_at);
  const callingCode = COUNTRIES.find((country) => country.name === facility.country)?.callingCode;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <FacilityAvatar name={facility.name} logoUrl={logoUrl} size="h-14 w-14" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{facility.name}</h1>
              <StatusBadge isActive={facility.is_active} />
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                Facility #{facility.facility_number}
              </span>
            </div>
            {facility.description && (
              <p className="mt-1 text-sm italic text-gray-400">{facility.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4" />
            Edit facility
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="rounded-xl border border-gray-200 p-2.5 text-gray-500 hover:bg-gray-50"
            >
              <EllipsisHorizontalIcon className="h-5 w-5" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onToggleActive();
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  {facility.is_active ? "Deactivate facility" : "Activate facility"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <hr className="my-4 border-gray-100" />

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600">
        {facility.address && (
          <span className="flex items-center gap-2">
            <MapPinIcon className="h-4 w-4 text-gray-400" />
            {facility.address}
          </span>
        )}
        {facility.phone_number && (
          <span className="flex items-center gap-2">
            <PhoneIcon className="h-4 w-4 text-gray-400" />
            {callingCode ? `+${callingCode} ` : ""}{formatPhoneDisplay(facility.phone_number)}
          </span>
        )}
        {facility.primary_domain && (
          <span className="flex items-center gap-2">
            <GlobeAltIcon className="h-4 w-4 text-gray-400" />
            {facility.primary_domain}
          </span>
        )}
        {createdDate && (
          <span className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-gray-400" />
            Created {createdDate}
          </span>
        )}
        {facility.timezone && (
          <span className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-gray-400" />
            {formatLocalTime(facility.timezone)} &middot; {facility.timezone}
          </span>
        )}
        {admins.length > 0 && (
          <span className="flex items-center gap-2">
            <span className="flex -space-x-2">
              {admins.slice(0, 3).map((admin) => (
                <span
                  key={admin.id}
                  title={admin.display_name || admin.email}
                  className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#EEF0FB] text-[10px] font-bold text-[#2B3674]"
                >
                  {initials(admin.display_name || admin.email)}
                </span>
              ))}
            </span>
            {admins.length} admin{admins.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
};

export default FacilityHeaderCard;
